// Agrega el token de Cognito (Authorization: Bearer <accessToken>) a TODAS las llamadas
// al backend (REACT_APP_STRANDS_API_URL), sin tocar cada fetch de la app.
//
// - Sólo aplica al origen del backend: Cognito, S3, Power BI, blob: y terceros pasan intactos.
// - Nunca toca Content-Type (los uploads con FormData necesitan que el navegador ponga el boundary).
// - Si el backend responde 401, reintenta una vez forzando la renovación del token.
// - Si no hay sesión (o falla Amplify), la petición sale sin header, igual que antes.
import { fetchAuthSession } from 'aws-amplify/auth';

const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001').origin;
  } catch {
    return null;
  }
})();

let pendingToken = null;

// Varias llamadas simultáneas al arrancar comparten la misma búsqueda del token.
function getAccessToken(forceRefresh = false) {
  if (!pendingToken || forceRefresh) {
    pendingToken = fetchAuthSession({ forceRefresh })
      .then((session) => session?.tokens?.accessToken?.toString() || null)
      .catch(() => null)
      .finally(() => {
        pendingToken = null;
      });
  }
  return pendingToken;
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input?.url || '';
}

function isBackendRequest(input) {
  if (!API_ORIGIN) return false;
  try {
    return new URL(requestUrl(input), window.location.href).origin === API_ORIGIN;
  } catch {
    return false;
  }
}

export function installAuthFetch() {
  if (typeof window === 'undefined' || window.__authFetchInstalled) return;
  window.__authFetchInstalled = true;
  const originalFetch = window.fetch.bind(window);

  async function send(input, init, forceRefresh) {
    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) {
      const token = await getAccessToken(forceRefresh);
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    return originalFetch(input, { ...init, headers });
  }

  window.fetch = async function authFetch(input, init = {}) {
    if (!isBackendRequest(input)) return originalFetch(input, init);
    const response = await send(input, init, false);
    if (response.status !== 401) return response;
    // Token vencido o revocado: una sola renovación forzada y reintento.
    return send(input, init, true);
  };
}
